import type { PermissionsGrants } from '@/types/permissionsGrants';

/** Whether a grant applies to a single team or the whole org. */
export type PermissionScope = 'team' | 'org';

/** A row is either a view or an edit grant state. */
export type PermissionMode = 'view' | 'edit';

export const PERMISSION_MODULE_IDS = [
  'fullAccess',
  'budgets',
  'agreements',
  'payments',
  'benchmarks',
  'agents',
  'rosters',
] as const;

export type PermissionModuleId = typeof PERMISSION_MODULE_IDS[number];

/**
 * Keys the permissions edit drawer can toggle.
 *
 * `PermissionsGrants` also includes derived flags (`canViewTotalEarnings`, `viewUserPermissions`, …),
 * wallet-ish fields, and `isSuperAdmin`. Those are not rows in this UI. Using `keyof PermissionsGrants`
 * would let a typo like `toggleGrant('isSuperAdmin', true)` compile; this union matches the grant rows
 * in `PERMISSION_MODULES` (`@/utils/UserPermissions`) only.
 */
export const PERMISSION_GRANT_KEYS = [
  'viewInstitutionalNil',
  'editInstitutionalNil',
  'viewThirdPartyNil',
  'editThirdPartyNil',
  'viewScholarships',
  'editScholarships',
  'viewRevShareAgreements',
  'editRevShareAgreements',
  'viewScholarshipAgreements',
  'editScholarshipAgreements',
  'viewPayments',
  'editPayments',
  'viewEarningsBenchmarks',
  'viewAgents',
  'editAgents',
  'viewRoster',
  'editRoster',
] as const satisfies ReadonlyArray<keyof PermissionsGrants>;

export type GrantKey = typeof PERMISSION_GRANT_KEYS[number];

/** Sparse draft of granted keys for the edit drawer (`true` only; absent means not granted). */
export type PermissionDraftMap = Partial<Record<GrantKey, true>>;

/** Full Access convenience rows — not stored grants; they select/clear other rows. */
export type PermissionHelperKind = 'selectAllView' | 'selectAllEdit';

/*  A grant row is a toggleable row that controls the state of a single permission */
export interface PermissionGrantRow {
  kind: 'grant';
  key: GrantKey;
  mode: PermissionMode;
  scope: PermissionScope;
  /** False for derived rows the user cannot toggle directly (e.g. Roster VIEW). */
  editable: boolean;
}

/* A helper row is not a grant; it is a toggleable row that controls the state of other rows. */
export interface PermissionHelperRow {
  kind: 'helper';
  helper: PermissionHelperKind;
}

export type PermissionRow = PermissionGrantRow | PermissionHelperRow;

export interface PermissionModule {
  id: PermissionModuleId;
  titleKey: string;
  rows: PermissionRow[];
}

// this is the checkbox state for the module header
export type PermissionModuleState = 'all' | 'some' | 'none';
